import React from 'react';
import { FileText, Download, Eye, Trash2 } from 'lucide-react';
import { getServerURL } from '../services/api';

const DocumentViewer = ({ documents, onDelete, showActions = true }) => {
  if (!documents || documents.length === 0) {
    return (
      <div className="p-6 bg-gray-50 rounded-xl border-2 border-dashed border-gray-300 text-center text-gray-500">
        <FileText className="w-12 h-12 mx-auto mb-2 text-gray-400" />
        <p>No documents uploaded</p>
      </div>
    );
  }

  const getDocumentUrl = (url) => {
    if (url.startsWith('http')) return url;
    return `${getServerURL()}${url}`;
  };

  const getDocumentIcon = (type) => {
    const iconClass = "w-10 h-10";
    switch (type) {
      case 'certificate':
        return <div className={`${iconClass} text-amber-600`}>🏆</div>;
      case 'warranty':
        return <div className={`${iconClass} text-blue-600`}>🛡️</div>;
      case 'appraisal':
        return <div className={`${iconClass} text-green-600`}>💎</div>;
      case 'invoice':
        return <div className={`${iconClass} text-slate-600`}>🧾</div>;
      case 'receipt':
        return <div className={`${iconClass} text-purple-600`}>📝</div>;
      default:
        return <FileText className={`${iconClass} text-gray-600`} />;
    }
  };

  const getDocumentTypeLabel = (type) => {
    return type.charAt(0).toUpperCase() + type.slice(1);
  };

  const handleView = (url) => {
    window.open(getDocumentUrl(url), '_blank');
  };

  const handleDownload = (url, name) => {
    const link = document.createElement('a');
    link.href = getDocumentUrl(url);
    link.download = name;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="space-y-3">
      {documents.map((doc, index) => (
        <div
          key={index}
          className="bg-gradient-to-r from-gray-50 to-white border-2 border-gray-200 rounded-2xl p-5 hover:border-slate-300 hover:shadow-lg transition-all"
        >
          <div className="flex items-center gap-4">
            {/* Document Icon */}
            <div className="flex-shrink-0">
              {getDocumentIcon(doc.type)}
            </div>

            {/* Document Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="flex-1 min-w-0">
                  <h4 className="text-base font-bold text-gray-900 mb-1">
                    {doc.name}
                  </h4>
                  <p className="text-sm text-gray-500">
                    <span className="inline-flex items-center px-3 py-1 rounded-full bg-slate-100 text-slate-700 font-semibold text-xs">
                      {getDocumentTypeLabel(doc.type)}
                    </span>
                  </p>
                </div>
              </div>

              {/* Actions */}
              {showActions && (
                <div className="flex items-center gap-2 flex-wrap">
                  <button
                    onClick={() => handleView(doc.url)}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-xl transition-colors"
                  >
                    <Eye className="w-4 h-4" />
                    View
                  </button>

                  <button
                    onClick={() => handleDownload(doc.url, doc.name)}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-blue-700 bg-blue-100 hover:bg-blue-200 rounded-xl transition-colors"
                  >
                    <Download className="w-4 h-4" />
                    Download
                  </button>

                  {onDelete && (
                    <button
                      onClick={() => {
                        if (window.confirm('Delete this document?')) {
                          onDelete(index);
                        }
                      }}
                      className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-red-700 bg-red-100 hover:bg-red-200 rounded-xl transition-colors ml-auto"
                    >
                      <Trash2 className="w-4 h-4" />
                      Delete
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default DocumentViewer;
